import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  useToast,
  VStack
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import fileDownload from 'js-file-download'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  FiCalendar,
  FiDownload,
  FiFileText,
  FiFilter,
  FiMail
} from 'react-icons/fi'

import Layout from '@component/Layout'
import Filter from '@component/Shared/Filter'

import filtersService from '@service/filters'
import parametrization, {
  IHistoricoExportarParams
} from '@service/parametrization'

const MAX_DAYS = 92

function getWeekRange() {
  const today = dayjs()
  const dayOfWeek = today.day()
  const monday = today.subtract(dayOfWeek === 0 ? 6 : dayOfWeek - 1, 'day')
  const saturday = monday.add(5, 'day')
  return {
    inicio: monday.format('YYYY-MM-DD'),
    final: saturday.format('YYYY-MM-DD')
  }
}

interface ISelectOption {
  label: string
  value: string | number
}

const WeeklyModifications: FC = () => {
  const toast = useToast()
  const weekRange = useMemo(() => getWeekRange(), [])

  const [inicio, setInicio] = useState(weekRange.inicio)
  const [final, setFinal] = useState(weekRange.final)
  const [loading, setLoading] = useState(false)
  const [loadingFilters, setLoadingFilters] = useState(false)

  const [empresasOptions, setEmpresasOptions] = useState<ISelectOption[]>([])
  const [lojasOptions, setLojasOptions] = useState<ISelectOption[]>([])

  const [selectedEmpresas, setSelectedEmpresas] = useState<ISelectOption[]>([])
  const [selectedLojas, setSelectedLojas] = useState<ISelectOption[]>([])
  const [usuariosInput, setUsuariosInput] = useState('')

  useEffect(() => {
    const loadFilters = async () => {
      setLoadingFilters(true)
      try {
        const [empresasRes, lojasRes] = await Promise.all([
          filtersService.getCompanies().catch(() => ({ data: { data: [] } })),
          filtersService.getCenters().catch(() => ({ data: { data: [] } }))
        ])

        const empresas = empresasRes.data?.data || []
        setEmpresasOptions(
          empresas.map((e: any) => ({
            label: `${e.value || e.id} - ${
              e.label || e.nome || e.descricao || ''
            }`,
            value: e.value || e.id
          }))
        )

        const lojas = lojasRes.data?.data || []
        setLojasOptions(
          lojas.map((l: any) => ({
            label: `${l.value || l.id} - ${
              l.label || l.nome || l.descricao || ''
            }`,
            value: l.value || l.id
          }))
        )
      } catch {
        // filters are optional
      } finally {
        setLoadingFilters(false)
      }
    }

    loadFilters()
  }, [])

  const daysDiff = useMemo(() => {
    if (!inicio || !final) return 0
    return dayjs(final).diff(dayjs(inicio), 'day')
  }, [inicio, final])

  const validationError = useMemo(() => {
    if (!inicio || !final) {
      return 'O periodo e obrigatorio'
    }
    if (dayjs(final).isBefore(dayjs(inicio))) {
      return 'A data inicial nao pode ser maior que a data final'
    }
    if (daysDiff + 1 > MAX_DAYS) {
      return `O periodo nao pode ultrapassar ${MAX_DAYS} dias`
    }
    return null
  }, [inicio, final, daysDiff])

  const handleExport = useCallback(async () => {
    if (validationError) {
      toast({
        title: 'Validacao',
        description: validationError,
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
      return
    }

    setLoading(true)
    try {
      const body: IHistoricoExportarParams = {
        periodo: { inicio, final }
      }

      if (selectedEmpresas.length > 0) {
        body.empresasIds = selectedEmpresas.map(e => Number(e.value))
      }
      if (selectedLojas.length > 0) {
        body.lojasIds = selectedLojas.map(l => Number(l.value))
      }

      const trimmedUsuarios = usuariosInput
        .split(/[,;]/)
        .map(u => u.trim())
        .filter(Boolean)
      if (trimmedUsuarios.length > 0) {
        body.usuarios = trimmedUsuarios
      }

      const { data: blob, headers } = await parametrization.postExportHistorico(
        body
      )

      const disposition = headers['content-disposition'] || ''
      const match = disposition.match(/filename[^;=\n]*=(.*)/i)
      const filename = match
        ? match[1].replace(/['"]/g, '').trim()
        : `PRM Abastecimento - Modificacoes_${dayjs().format('YYYY-MM-DD')}.csv`

      fileDownload(blob, filename)

      toast({
        title: 'Exportacao concluida',
        description: 'O arquivo CSV foi baixado com sucesso.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
    } catch (error: any) {
      let message = 'Ocorreu um erro ao exportar. Tente novamente.'

      if (error.response) {
        const { status } = error.response

        if (status === 400) {
          try {
            const text =
              error.response.data instanceof Blob
                ? await error.response.data.text()
                : JSON.stringify(error.response.data)
            const parsed = JSON.parse(text)
            message = parsed.Message || message
          } catch {
            // keep default message
          }
        } else if (status === 401) {
          message = 'Sessao expirada. Faca login novamente.'
        } else if (status === 403) {
          message = 'Voce nao tem permissao para acessar este recurso.'
        } else if (status === 406) {
          message = 'Suas permissoes foram alteradas. Faca login novamente.'
        } else if (status === 423) {
          message = 'Sistema em manutencao. Tente novamente mais tarde.'
        }
      }

      toast({
        title: 'Erro na exportacao',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      })
    } finally {
      setLoading(false)
    }
  }, [
    inicio,
    final,
    selectedEmpresas,
    selectedLojas,
    usuariosInput,
    validationError,
    toast
  ])

  const periodoFormatado = useMemo(() => {
    if (!inicio || !final) return ''
    return `${dayjs(inicio).format('DD/MM/YYYY')} - ${dayjs(final).format(
      'DD/MM/YYYY'
    )}`
  }, [inicio, final])

  return (
    <Layout title="Relatorio de Modificacoes Semanais">
      <Box bg="gray.50" minH="calc(100vh - 60px)">
        {/* Header */}
        <Box
          bg="white"
          borderBottom="1px solid"
          borderColor="gray.100"
          px={8}
          py={5}
        >
          <Flex align="center" justify="space-between" maxW="1200px" mx="auto">
            <HStack spacing={4}>
              <Flex
                align="center"
                justify="center"
                w="42px"
                h="42px"
                bg="green.50"
                borderRadius="10px"
              >
                <Icon as={FiFileText} color="green.600" boxSize={5} />
              </Flex>
              <Box>
                <Text fontSize="lg" fontWeight="600" color="gray.800">
                  Relatorio de Modificacoes
                </Text>
                <Text fontSize="xs" color="gray.500">
                  Historico de alteracoes de parametros de abastecimento
                </Text>
              </Box>
            </HStack>
            {periodoFormatado && !validationError && (
              <Badge
                bg="green.600"
                color="white"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
                fontWeight="600"
              >
                {daysDiff + 1} dias selecionados
              </Badge>
            )}
          </Flex>
        </Box>

        {/* Content */}
        <Box maxW="1200px" mx="auto" px={8} py={6}>
          {/* Period Card */}
          <Box
            bg="white"
            borderRadius="12px"
            border="1px solid"
            borderColor="gray.100"
            p={6}
            mb={4}
            transition="box-shadow 0.2s"
            _hover={{ boxShadow: 'sm' }}
          >
            <HStack spacing={2} mb={4}>
              <Icon as={FiCalendar} color="green.500" boxSize={4} />
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                Periodo
              </Text>
              <Badge colorScheme="red" variant="subtle" fontSize="10px">
                Obrigatorio
              </Badge>
            </HStack>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.500" mb={1}>
                  Data Inicio
                </FormLabel>
                <Input
                  type="date"
                  value={inicio}
                  onChange={e => setInicio(e.target.value)}
                  size="sm"
                  borderRadius="8px"
                  borderColor="gray.200"
                  _hover={{ borderColor: 'gray.300' }}
                  _focus={{
                    borderColor: 'green.400',
                    boxShadow: '0 0 0 1px #48BB78'
                  }}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="gray.500" mb={1}>
                  Data Final
                </FormLabel>
                <Input
                  type="date"
                  value={final}
                  onChange={e => setFinal(e.target.value)}
                  size="sm"
                  borderRadius="8px"
                  borderColor="gray.200"
                  _hover={{ borderColor: 'gray.300' }}
                  _focus={{
                    borderColor: 'green.400',
                    boxShadow: '0 0 0 1px #48BB78'
                  }}
                />
              </FormControl>
            </Grid>

            {validationError && (
              <HStack mt={3} p={2} bg="red.50" borderRadius="8px" spacing={2}>
                <Box
                  w="4px"
                  h="100%"
                  minH="16px"
                  bg="red.400"
                  borderRadius="full"
                />
                <Text fontSize="xs" color="red.600" fontWeight="500">
                  {validationError}
                </Text>
              </HStack>
            )}

            {!validationError && inicio && final && (
              <Text fontSize="xs" color="gray.400" mt={2}>
                Periodo: {periodoFormatado} ({daysDiff + 1} dias) — Maximo{' '}
                {MAX_DAYS} dias
              </Text>
            )}
          </Box>

          {/* Filters Card */}
          <Box
            bg="white"
            borderRadius="12px"
            border="1px solid"
            borderColor="gray.100"
            p={6}
            mb={4}
            transition="box-shadow 0.2s"
            _hover={{ boxShadow: 'sm' }}
          >
            <HStack spacing={2} mb={4}>
              <Icon as={FiFilter} color="green.500" boxSize={4} />
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                Filtros
              </Text>
              <Badge colorScheme="gray" variant="subtle" fontSize="10px">
                Opcional
              </Badge>
            </HStack>

            <Grid
              templateColumns={{ base: '1fr', md: '1fr 1fr' }}
              gap={4}
              mb={4}
            >
              <GridItem>
                <Filter
                  label="Empresas"
                  options={empresasOptions}
                  value={selectedEmpresas}
                  isMulti
                  isLoading={loadingFilters}
                  onChange={(val: any) => setSelectedEmpresas(val || [])}
                />
              </GridItem>
              <GridItem>
                <Filter
                  label="Lojas"
                  options={lojasOptions}
                  value={selectedLojas}
                  isMulti
                  isLoading={loadingFilters}
                  onChange={(val: any) => setSelectedLojas(val || [])}
                />
              </GridItem>
            </Grid>

            <Divider mb={4} borderColor="gray.100" />

            <FormControl>
              <FormLabel fontSize="sm" color="gray.600" mb={1}>
                Usuarios
              </FormLabel>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiMail} color="gray.400" boxSize={3.5} />
                </InputLeftElement>
                <Input
                  type="text"
                  placeholder="fulano@empresa.com, ciclano@empresa.com"
                  value={usuariosInput}
                  onChange={e => setUsuariosInput(e.target.value)}
                  borderRadius="8px"
                  borderColor="gray.200"
                  _hover={{ borderColor: 'gray.300' }}
                  _focus={{
                    borderColor: 'green.400',
                    boxShadow: '0 0 0 1px #48BB78'
                  }}
                  pl={9}
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.400" mt={1}>
                Separe os e-mails por virgula ou ponto e virgula
              </Text>
            </FormControl>
          </Box>

          {/* Action Bar */}
          <Box
            bg="white"
            borderRadius="12px"
            border="1px solid"
            borderColor="gray.100"
            p={5}
          >
            <Flex
              align="center"
              justify="space-between"
              direction={{ base: 'column', md: 'row' }}
              gap={4}
            >
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="sm" color="gray.600" fontWeight="500">
                  Pronto para exportar
                </Text>
                <Text fontSize="xs" color="gray.400">
                  O arquivo sera gerado em formato CSV com separador ponto e
                  virgula (;)
                </Text>
              </VStack>

              <Button
                colorScheme="green"
                leftIcon={<FiDownload />}
                onClick={handleExport}
                isLoading={loading}
                loadingText="Exportando..."
                isDisabled={!!validationError || loading}
                size="md"
                borderRadius="8px"
                px={8}
                fontWeight="500"
                boxShadow={
                  !validationError && !loading
                    ? '0 1px 3px rgba(72, 187, 120, 0.4)'
                    : 'none'
                }
                _hover={{
                  transform:
                    !validationError && !loading ? 'translateY(-1px)' : 'none',
                  boxShadow:
                    !validationError && !loading
                      ? '0 4px 12px rgba(72, 187, 120, 0.4)'
                      : 'none'
                }}
                _active={{
                  transform: 'translateY(0)',
                  boxShadow: '0 1px 3px rgba(72, 187, 120, 0.4)'
                }}
                transition="all 0.2s"
              >
                Exportar CSV
              </Button>
            </Flex>
          </Box>
        </Box>
      </Box>
    </Layout>
  )
}

export default WeeklyModifications
